import { useEffect, useState } from 'react';
import { getFormattedAmount } from '@zonofi/common/functions/format-amount';
import { OrderStatusEnum, SortDirection } from '../../../constants';
import {
  getWorkspaceId,
  useAppSelector,
  useGetAllInvoiceMutation,
  useListOrdersMutation,
} from '@zonofi/zono-money-store';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { SVGS } from '../../../assets';

export const useReferenceDetails = () => {
  const { customerId = '' } = useParams();
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [fetchInvoice] = useGetAllInvoiceMutation();
  const [fetchOrders] = useListOrdersMutation();

  const [orderOption, setOrderOption] = useState<
    {
      id: string;
      name: string | undefined;
      date: string;
      value: string;
    }[]
  >([]);
  const [invoiceOption, setInvoiceOption] = useState<
    {
      id: string;
      name: string;
      date: string;
      value: string;
    }[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getInvoice = () => {
    return fetchInvoice({
      workspaceId: sellerWorkspaceId,
    }).unwrap();
  };

  const getOrders = () => {
    return fetchOrders({
      params: {
        workspaceId: sellerWorkspaceId,
        customerId: customerId,
      },
      body: {
        workspaceId: sellerWorkspaceId,
        customerId: customerId,
        skip: 1,
        sortBy: 'orderPlacedAt',
        sortDirection: SortDirection.DESCENDING,
        includeInvoice: true,
        startDate: moment().subtract(1, 'year').startOf('year').format(),
        endDate: moment().endOf('day').format(),
        pageNo: 1,
        pageSize: 50,
        filterModel: {
          headDivisionIds: [],
          divisionIds: [],
          cfaIds: [],
          status: [
            OrderStatusEnum.APPROVED,
            OrderStatusEnum.CONFIRMED,
            OrderStatusEnum.WAITING_FOR_CONFIRMATION,
          ],
          customerIds: [customerId],
        },
      },
    }).unwrap();
  };

  const fetchReferenceData = async () => {
    setIsLoading(true);
    const [invoiceResult, ordersResult] = await Promise.allSettled([
      getInvoice(),
      getOrders(),
    ]);

    if (invoiceResult.status === 'fulfilled') {
      const invoices = invoiceResult.value?.invoices ?? [];
      const mappedInvoices = invoices.map((item: any) => ({
        id: item.id.toString(),
        name: item.docNumber || `Invoice #${item.id}`,
        date: moment(item.invoiceDate).format('DD/MM/YYYY'),
        value: getFormattedAmount(item.invoiceAmount, true),
      }));

      setInvoiceOption(mappedInvoices);
    }

    if (ordersResult.status === 'fulfilled') {
      const orders = ordersResult.value?.order ?? [];
      const mappedOrders = orders.map((item: any) => ({
        id: item.id.toString(),
        name: item?.orderMetaData?.refOrderNumber || `Order #${item.id}`,
        date: moment(item.documentDate).format('DD/MM/YYYY'),
        value: getFormattedAmount(parseInt(item.subTotalWithTaxAmount)),
      }));

      setOrderOption(mappedOrders);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchReferenceData();
  }, []);

  const reference_Details = [
    {
      key: 'orderIds',
      label: 'Please select the order number & invoice number for the dispatch',
      placeholder: 'Select order',
      icon: SVGS.OrdersBlackIcon,
      data: orderOption,
      multiple: true,
      emptyMessage: 'No orders found for this customer',
    },
    {
      key: 'invoiceIds',
      label: '',
      placeholder: 'Select Invoice',
      icon: SVGS.SendInvoice,
      data: invoiceOption,
      multiple: false,
      emptyMessage: 'No invoices found for this customer',
    },
  ];

  return { reference_Details, isLoading };
};
